import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Stack,
  FormControl,
  OutlinedInput,
  Typography,
  Button,
  Select,
  MenuItem,
  Switch,
  FormHelperText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import { Upload } from "react-iconly";
import { Formik } from "formik";
import * as yup from "yup";
import "./index.scss";
import { Images } from "./../../assets/common";
import ApiService from "../../services/api-service";
import {
  getErrorString,
  validateForm,
  isEmpty,
} from "../../constants/function";
import notifier from "../../components/Notifier";
import SuccessPopup from "../../components/SuccessPopup";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";

const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  studioId: yup.string().required("Please select a studio/playlist"),
  videoFile: yup.mixed().required("Video file is required"),
  explicitContent: yup.boolean(),
});

export default function UploadVideo() {
  const { t } = useTranslation("common");
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [studios, setStudios] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [musicQuery, setMusicQuery] = useState("");
  const [musicResults, setMusicResults] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [musicSearching, setMusicSearching] = useState(false);
  const [musicStartTime, setMusicStartTime] = useState(0);
  const [musicEndTime, setMusicEndTime] = useState(30);
  const [waveformReady, setWaveformReady] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [songDurationState, setSongDurationState] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [musicVideoOffset, setMusicVideoOffset] = useState(0);
  const [muteVideoAudio, setMuteVideoAudio] = useState(true);
  const [subscribersOnly, setSubscribersOnly] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const addTags = (text) => {
    const next = [...tags];
    text.split(",").forEach((raw) => {
      const t = raw.trim().toLowerCase().replace(/^#/, "").replace(/\s+/g, " ").slice(0, 30);
      if (t && !next.includes(t) && next.length < 10) next.push(t);
    });
    setTags(next);
    setTagInput("");
  };
  const [videoRenderedWidth, setVideoRenderedWidth] = useState(0);
  const musicSearchTimer = useRef(null);
  const waveformContainerRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const timelineRef = useRef(null);
  const draggingRef = useRef(null); // 'move' | 'left' | 'right' | null

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      const response = await ApiService.PLAYLIST({}, true);
      setStudios(response.result || []);
    } catch (error) {
      console.error("Error fetching studios:", error);
      notifier.error("Failed to load studios/playlists");
    }
  };

  // Track rendered video width so the timeline matches
  useEffect(() => {
    const el = videoPreviewRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setVideoRenderedWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [videoPreview]);

  // Two-way sync: when timeline changes musicStartTime/musicEndTime, update the waveform region
  useEffect(() => {
    const region = regionRef.current;
    if (!region) return;
    const needsUpdate =
      Math.abs(region.start - musicStartTime) > 0.05 ||
      Math.abs(region.end - musicEndTime) > 0.05;
    if (needsUpdate) {
      region.setOptions({ start: musicStartTime, end: musicEndTime });
    }
  }, [musicStartTime, musicEndTime]);

  // Initialize waveform when a song is selected
  useEffect(() => {
    if (!selectedMusic?.fileId?.filePath || !waveformContainerRef.current) return;

    const audioUrl = selectedMusic.fileId.filePath;
    const songDuration = selectedMusic.duration || 180;

    // Audio element for playback (no crossOrigin to avoid CORS)
    const audio = document.createElement("audio");

    const ws = WaveSurfer.create({
      container: waveformContainerRef.current,
      media: audio,
      waveColor: "#656666",
      progressColor: "#EE772F",
      barWidth: 3,
      barGap: 2,
      barRadius: 2,
      barHeight: 0.7,
      height: 80,
      normalize: true,
    });

    const regions = ws.registerPlugin(RegionsPlugin.create());

    let cancelled = false;

    const loadAudio = async () => {
      let peaks = null;
      try {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        audioCtx.close();
        if (cancelled) return;

        const channel = audioBuffer.getChannelData(0);
        const numPeaks = 500;
        const samplesPerPeak = Math.floor(channel.length / numPeaks);
        const rms = new Float32Array(numPeaks);
        for (let i = 0; i < numPeaks; i++) {
          let sum = 0;
          const start = i * samplesPerPeak;
          const end = Math.min(start + samplesPerPeak, channel.length);
          for (let j = start; j < end; j++) sum += channel[j] * channel[j];
          rms[i] = Math.sqrt(sum / (end - start));
        }
        let maxPeak = 0;
        for (let i = 0; i < rms.length; i++) if (rms[i] > maxPeak) maxPeak = rms[i];
        if (maxPeak > 0) for (let i = 0; i < rms.length; i++) rms[i] /= maxPeak;
        peaks = rms;
      } catch (e) {
        if (cancelled) return;
        peaks = new Float32Array(500);
        for (let i = 0; i < 500; i++) peaks[i] = 0.3 + Math.random() * 0.4;
      }

      if (cancelled) return;

      try {
        await ws.load(audioUrl, [peaks], songDuration);
      } catch (e) {
        console.error("[WaveformClip] Failed to load audio", e);
      }
    };

    ws.on("ready", () => {
      if (cancelled) return;
      const dur = ws.getDuration() || songDuration;
      setSongDurationState(dur);
      const defaultEnd = Math.min(30, dur);

      const region = regions.addRegion({
        start: 0,
        end: defaultEnd,
        color: "rgba(255, 238, 32, 0.3)",
        drag: true,
        resize: true,
        minLength: 5,
      });

      regionRef.current = region;
      setMusicStartTime(0);
      setMusicEndTime(parseFloat(defaultEnd.toFixed(1)));
      setWaveformReady(true);

      // When region is dragged/resized, play from new start
      region.on("update-end", () => {
        const s = parseFloat(region.start.toFixed(1));
        const e = parseFloat(region.end.toFixed(1));
        setMusicStartTime(s);
        setMusicEndTime(e);
        // Auto-play from clip start after dragging
        ws.setTime(s);
        ws.play();
        setPreviewPlaying(true);
      });
    });

    // Track playback position and stop at clip end (only when standalone, not video-driven)
    ws.on("timeupdate", (time) => {
      if (cancelled) return;
      setPreviewTime(time);
      const r = regionRef.current;
      const videoPaused = !videoPreviewRef.current || videoPreviewRef.current.paused;
      if (r && time >= r.end && videoPaused) {
        ws.pause();
        ws.setTime(r.start);
        setPreviewPlaying(false);
      }
    });

    ws.on("play", () => { if (!cancelled) setPreviewPlaying(true); });
    ws.on("pause", () => { if (!cancelled) setPreviewPlaying(false); });

    wavesurferRef.current = ws;
    loadAudio();

    return () => {
      cancelled = true;
      audio.pause();
      audio.src = "";
      ws.destroy();
      wavesurferRef.current = null;
      regionRef.current = null;
      setWaveformReady(false);
      setPreviewPlaying(false);
      setPreviewTime(0);
    };
  }, [selectedMusic]);

  const handlePreviewPlayPause = () => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    if (previewPlaying) {
      ws.pause();
      // Also pause the video
      if (videoPreviewRef.current && !videoPreviewRef.current.paused) {
        videoPreviewRef.current.pause();
      }
    } else {
      // If outside clip range, seek to clip start first
      const r = regionRef.current;
      if (r) {
        const time = ws.getCurrentTime();
        if (time < r.start || time >= r.end) {
          ws.setTime(r.start);
        }
      }
      ws.play();
      // Sync the video: seek to where this song position maps in the video
      if (videoPreviewRef.current && videoDuration > 0) {
        const songTime = ws.getCurrentTime();
        const videoTime = musicVideoOffset + (songTime - musicStartTime);
        if (videoTime >= 0 && videoTime < videoDuration) {
          videoPreviewRef.current.currentTime = videoTime;
          videoPreviewRef.current.play().catch(() => {});
        }
      }
    }
  };

  const handlePreviewStop = () => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    ws.pause();
    const r = regionRef.current;
    if (r) ws.setTime(r.start);
    setPreviewPlaying(false);
    // Also pause the video
    if (videoPreviewRef.current && !videoPreviewRef.current.paused) {
      videoPreviewRef.current.pause();
    }
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleMusicSearch = (query) => {
    setMusicQuery(query);
    if (musicSearchTimer.current) clearTimeout(musicSearchTimer.current);
    if (!query.trim()) {
      setMusicResults([]);
      return;
    }
    musicSearchTimer.current = setTimeout(async () => {
      try {
        setMusicSearching(true);
        const resp = await ApiService.SEARCH_MUSIC(query);
        setMusicResults(resp.result || []);
      } catch (e) {
        console.error("Music search failed:", e);
      } finally {
        setMusicSearching(false);
      }
    }, 300);
  };

  const handleVideoChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-flv', 'video/webm'];
      if (!validTypes.includes(file.type)) {
        notifier.error("Please select a valid video file (MP4, MPEG, MOV, AVI, FLV, WebM)");
        return;
      }

      const maxSize = 2 * 1024 * 1024 * 1024;
      if (file.size > maxSize) {
        notifier.error("Video file size must be less than 2GB");
        return;
      }

      setSelectedVideo(file);
      setFieldValue("videoFile", file);
      
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append("file", values.videoFile, values.videoFile.name);
      formData.append("title", values.title);
      formData.append("studioId", values.studioId);
      formData.append("isExplicit", values.explicitContent ? 'true' : 'false');
      formData.append("subscribersOnly", subscribersOnly ? 'true' : 'false');
      if (tags.length) formData.append("tags", JSON.stringify(tags));
      if (selectedMusic) {
        formData.append("musicId", selectedMusic._id);
        if (musicStartTime != null) formData.append("musicStartTime", musicStartTime);
        if (musicEndTime != null) formData.append("musicEndTime", musicEndTime);
        formData.append("musicVideoOffset", musicVideoOffset);
        formData.append("muteVideoAudio", muteVideoAudio);
      }

      const response = await ApiService.UPVID(values.studioId, formData);
      
      if (response.code === 200) {
        setSuccess(true);
        setTimeout(() => {
          history.push('/app/Library');
        }, 2000);
      } else {
        notifier.error(response.message || "Failed to upload video");
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    history.push('/app/Library');
  };

  return (
    <Box className="upload-video-page">
      <PageHeader title={t("Upload Video")} />
      
      <Box className="upload-video-container" sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
        <Formik
          initialValues={{
            title: "",
            studioId: "",
            videoFile: null,
            explicitContent: false,
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Select Studio/Playlist *
                  </Typography>
                  <FormControl fullWidth error={touched.studioId && !!errors.studioId}>
                    <Select
                      name="studioId"
                      value={values.studioId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      displayEmpty
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a studio/playlist</em>
                      </MenuItem>
                      {studios.length === 0 ? (
                        <MenuItem disabled>
                          <em>No studios found. Please create one first.</em>
                        </MenuItem>
                      ) : (
                        studios.map((studio) => (
                          <MenuItem key={studio._id} value={studio._id}>
                            {studio.title}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.studioId && errors.studioId && (
                      <FormHelperText>{errors.studioId}</FormHelperText>
                    )}
                  </FormControl>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Don't have a studio?
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => history.push('/app/Playlists/create')}
                      sx={{
                        color: "#ffee20",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: "rgba(255, 238, 32, 0.1)",
                        },
                      }}
                    >
                      Create one
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Video Title *
                  </Typography>
                  <OutlinedInput
                    name="title"
                    placeholder="Enter video title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.title && !!errors.title}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: touched.title && errors.title ? "error.main" : "rgba(255, 255, 255, 0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: touched.title && errors.title ? "error.main" : "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  />
                  {touched.title && errors.title && (
                    <FormHelperText error>{errors.title}</FormHelperText>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Upload Video *
                  </Typography>

                  {/* Video preview — shown outside the upload box so controls work */}
                  {videoPreview && (
                    <Box sx={{ mb: 2, textAlign: "center" }}>
                      <video
                        ref={videoPreviewRef}
                        src={videoPreview}
                        controls
                        disablePictureInPicture
                        muted={muteVideoAudio}
                        onLoadedMetadata={(e) => {
                          const dur = e.target.duration;
                          if (dur && isFinite(dur)) setVideoDuration(dur);
                          setVideoRenderedWidth(e.target.clientWidth);
                        }}
                        onPlay={(e) => {
                          // Sync WaveSurfer to the video position
                          const ws = wavesurferRef.current;
                          if (!ws || !selectedMusic) return;
                          // If WaveSurfer is already playing (waveform button started it), skip
                          if (ws.isPlaying()) return;
                          const vt = e.target.currentTime;
                          const clipDur = musicEndTime - musicStartTime;
                          const clipEnd = musicVideoOffset + clipDur;
                          if (vt >= musicVideoOffset && vt < clipEnd) {
                            ws.setTime(musicStartTime + (vt - musicVideoOffset));
                            ws.play();
                          }
                        }}
                        onTimeUpdate={(e) => {
                          const video = e.target;
                          setVideoCurrentTime(video.currentTime);
                          if (video.paused) return;
                          const ws = wavesurferRef.current;
                          if (!ws || !selectedMusic) return;
                          const vt = video.currentTime;
                          const clipDur = musicEndTime - musicStartTime;
                          const clipEnd = musicVideoOffset + clipDur;
                          if (vt >= musicVideoOffset && vt < clipEnd) {
                            // Keep WaveSurfer in sync with video time
                            const songTime = musicStartTime + (vt - musicVideoOffset);
                            if (Math.abs(ws.getCurrentTime() - songTime) > 0.3) {
                              ws.setTime(songTime);
                            }
                            if (!ws.isPlaying()) ws.play();
                          } else {
                            if (ws.isPlaying()) ws.pause();
                          }
                        }}
                        onPause={() => {
                          if (wavesurferRef.current?.isPlaying?.()) wavesurferRef.current.pause();
                        }}
                        onSeeked={(e) => {
                          setVideoCurrentTime(e.target.currentTime);
                          const ws = wavesurferRef.current;
                          if (!ws || !selectedMusic) return;
                          const vt = e.target.currentTime;
                          const clipDur = musicEndTime - musicStartTime;
                          const clipEnd = musicVideoOffset + clipDur;
                          if (vt >= musicVideoOffset && vt < clipEnd) {
                            ws.setTime(musicStartTime + (vt - musicVideoOffset));
                          }
                        }}
                        onEnded={() => {
                          if (wavesurferRef.current?.isPlaying?.()) wavesurferRef.current.pause();
                        }}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          borderRadius: "8px",
                        }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 1 }}>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                          {selectedVideo?.name}
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setSelectedVideo(null);
                            setVideoPreview(null);
                            setVideoDuration(0);
                            setVideoRenderedWidth(0);
                            setFieldValue("videoFile", null);
                          }}
                          sx={{ color: "#ff5252", textTransform: "none" }}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Upload area — only show the click-to-upload box when no video selected */}
                  {!videoPreview && (
                    <Box
                      sx={{
                        border: "2px dashed rgba(255, 255, 255, 0.3)",
                        borderRadius: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        p: 4,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#ffee20",
                          backgroundColor: "rgba(255, 238, 32, 0.05)",
                        },
                      }}
                      onClick={() => document.getElementById("video-upload").click()}
                    >
                      <Upload size={48} style={{ marginBottom: 16, color: "#ffee20" }} />
                      <Typography variant="h6">
                        Click to upload video
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                        MP4, MPEG, MOV, AVI, FLV, WebM (Max 2GB)
                      </Typography>
                    </Box>
                  )}
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleVideoChange(e, setFieldValue)}
                  />
                  {touched.videoFile && errors.videoFile && (
                    <FormHelperText error>{errors.videoFile}</FormHelperText>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Explicit Content
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Does this video contain explicit content?
                    </Typography>
                  </Box>
                  <Switch
                    name="explicitContent"
                    checked={values.explicitContent}
                    onChange={(e) => setFieldValue("explicitContent", e.target.checked)}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Tags <Typography component="span" variant="caption" sx={{ opacity: 0.6 }}>{tags.length}/10</Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 1 }}>
                    Words people would search for. Press Enter or comma after each one.
                  </Typography>
                  <OutlinedInput
                    fullWidth
                    value={tagInput}
                    placeholder="e.g. music video, live, behind the scenes"
                    onChange={(e) => (e.target.value.endsWith(",") ? addTags(e.target.value) : setTagInput(e.target.value))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTags(tagInput); } }}
                    onBlur={() => tagInput.trim() && addTags(tagInput)}
                    sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: 2, mb: 1 }}
                  />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {tags.map((t) => (
                      <Chip key={t} label={`#${t}`} onDelete={() => setTags(tags.filter((x) => x !== t))} sx={{ backgroundColor: "#ffee20", color: "#141410", fontWeight: 700 }} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Subscribers only
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Only your subscribers can watch this video.
                    </Typography>
                  </Box>
                  <Switch
                    checked={subscribersOnly}
                    onChange={(e) => setSubscribersOnly(e.target.checked)}
                  />
                </Box>

                {/* Background Music Selection */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Background Music (Optional)
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 1 }}>
                    Select a song to play as background music. The artist earns royalties when viewers watch.
                  </Typography>

                  {selectedMusic ? (
                    <Box>
                      <Chip
                        icon={<MusicNoteIcon />}
                        label={`${selectedMusic.title} - ${selectedMusic.uploader?.displayArtistName || selectedMusic.uploader?.firstName || "Unknown"}`}
                        onDelete={() => {
                          setSelectedMusic(null);
                          setMusicQuery("");
                          setMusicResults([]);
                        }}
                        deleteIcon={<CloseIcon />}
                        sx={{
                          backgroundColor: "rgba(255, 238, 32, 0.15)",
                          color: "#ffee20",
                          "& .MuiChip-deleteIcon": { color: "#ffee20" },
                          "& .MuiChip-icon": { color: "#ffee20" },
                          py: 2.5,
                          fontSize: "0.95rem",
                        }}
                      />
                      {/* Waveform clip selector */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.7, mb: 0.5, display: "block" }}>
                          Drag the yellow region to select which part plays. Click anywhere on the waveform to seek.
                        </Typography>
                        <Box
                          ref={waveformContainerRef}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: 1,
                            p: 1,
                            minHeight: 80,
                            position: "relative",
                          }}
                        />
                        {waveformReady && (
                          <Box sx={{ mt: 1 }}>
                            {/* Play/Pause/Stop controls */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <IconButton
                                size="small"
                                onClick={handlePreviewPlayPause}
                                sx={{ color: "#ffee20", bgcolor: "rgba(255, 238, 32, 0.1)", "&:hover": { bgcolor: "rgba(255, 238, 32, 0.2)" } }}
                              >
                                {previewPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handlePreviewStop}
                                sx={{ color: "#fff", opacity: 0.7, "&:hover": { opacity: 1 } }}
                              >
                                <StopIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="caption" sx={{ opacity: 0.7, ml: 0.5 }}>
                                {formatTime(previewTime)} / {formatTime(songDurationState)}
                              </Typography>
                              <Box sx={{ flex: 1 }} />
                              <Typography variant="body2" sx={{ color: "#ffee20", fontWeight: 600 }}>
                                {formatTime(musicStartTime)} → {formatTime(musicEndTime)}
                                <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7, color: "#fff" }}>
                                  ({(musicEndTime - musicStartTime).toFixed(0)}s clip)
                                </Typography>
                              </Typography>
                            </Box>
                            {/* Scrub slider for full song */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.5, minWidth: 32 }}>
                                {formatTime(previewTime)}
                              </Typography>
                              <input
                                type="range"
                                min="0"
                                max={songDurationState || 1}
                                step="0.1"
                                value={previewTime}
                                onChange={(e) => {
                                  const t = parseFloat(e.target.value);
                                  if (wavesurferRef.current) {
                                    wavesurferRef.current.setTime(t);
                                    setPreviewTime(t);
                                  }
                                }}
                                style={{ flex: 1, accentColor: "#ffee20" }}
                              />
                              <Typography variant="caption" sx={{ opacity: 0.5, minWidth: 32, textAlign: "right" }}>
                                {formatTime(songDurationState)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Video Timeline — position the music clip */}
                      {videoDuration > 0 && waveformReady && (
                        <Box sx={{ mt: 2, width: videoRenderedWidth > 0 ? videoRenderedWidth : "100%" }}>
                          <Typography variant="caption" sx={{ opacity: 0.7, mb: 0.5, display: "block" }}>
                            Drag the block to position the music in the video. Resize edges to trim.
                          </Typography>
                          <Box
                            ref={timelineRef}
                            sx={{
                              position: "relative",
                              height: 40,
                              bgcolor: "rgba(255,255,255,0.08)",
                              borderRadius: 1,
                              overflow: "visible",
                              cursor: "default",
                              userSelect: "none",
                            }}
                            onMouseDown={(e) => {
                              // Only start drag if clicking on the block
                              const rect = timelineRef.current.getBoundingClientRect();
                              const x = e.clientX - rect.left;
                              const pxPerSec = rect.width / videoDuration;
                              const clipDur = musicEndTime - musicStartTime;
                              const blockLeft = musicVideoOffset * pxPerSec;
                              const blockRight = (musicVideoOffset + clipDur) * pxPerSec;
                              const handleSize = 10;

                              // Check if click is near the playback cursor (±8px) — seek takes priority
                              const cursorX = (videoCurrentTime / videoDuration) * rect.width;
                              const nearCursor = Math.abs(x - cursorX) < 8;

                              let mode = null;
                              if (!nearCursor) {
                                if (x >= blockLeft && x <= blockLeft + handleSize) {
                                  mode = "left";
                                } else if (x >= blockRight - handleSize && x <= blockRight) {
                                  mode = "right";
                                } else if (x >= blockLeft && x <= blockRight) {
                                  mode = "move";
                                }
                              }

                              // Click near cursor or outside the block = seek video to that position
                              if (!mode) {
                                const seekTime = Math.max(0, Math.min(videoDuration, x / pxPerSec));
                                setVideoCurrentTime(seekTime);
                                if (videoPreviewRef.current) {
                                  videoPreviewRef.current.currentTime = seekTime;
                                }
                                // Allow dragging to scrub
                                draggingRef.current = { mode: "seek" };
                                e.preventDefault();

                                const onMouseMove = (ev) => {
                                  const d = draggingRef.current;
                                  if (!d || d.mode !== "seek") return;
                                  const mx = ev.clientX - rect.left;
                                  const t = Math.max(0, Math.min(videoDuration, mx / pxPerSec));
                                  setVideoCurrentTime(t);
                                  if (videoPreviewRef.current) {
                                    videoPreviewRef.current.currentTime = t;
                                  }
                                };

                                const onMouseUp = () => {
                                  draggingRef.current = null;
                                  window.removeEventListener("mousemove", onMouseMove);
                                  window.removeEventListener("mouseup", onMouseUp);
                                };

                                window.addEventListener("mousemove", onMouseMove);
                                window.addEventListener("mouseup", onMouseUp);
                                return;
                              }

                              draggingRef.current = { mode, startX: e.clientX, startOffset: musicVideoOffset, startSongStart: musicStartTime, startSongEnd: musicEndTime };
                              e.preventDefault();

                              const onMouseMove = (ev) => {
                                const d = draggingRef.current;
                                if (!d) return;
                                const dx = ev.clientX - d.startX;
                                const dtSec = dx / pxPerSec;

                                if (d.mode === "move") {
                                  const newOffset = Math.max(0, Math.min(videoDuration - (d.startSongEnd - d.startSongStart), d.startOffset + dtSec));
                                  setMusicVideoOffset(parseFloat(newOffset.toFixed(1)));
                                } else if (d.mode === "left") {
                                  // Shrink from left = increase musicStartTime
                                  const newStart = Math.max(0, Math.min(d.startSongEnd - 5, d.startSongStart + dtSec));
                                  setMusicStartTime(parseFloat(newStart.toFixed(1)));
                                  // Also adjust video offset so left edge moves
                                  const newOffset = d.startOffset + (newStart - d.startSongStart);
                                  setMusicVideoOffset(parseFloat(Math.max(0, newOffset).toFixed(1)));
                                } else if (d.mode === "right") {
                                  // Shrink from right = decrease musicEndTime
                                  const maxEnd = songDurationState || (d.startSongEnd + 60);
                                  const newEnd = Math.max(d.startSongStart + 5, Math.min(maxEnd, d.startSongEnd + dtSec));
                                  setMusicEndTime(parseFloat(newEnd.toFixed(1)));
                                }
                              };

                              const onMouseUp = () => {
                                draggingRef.current = null;
                                window.removeEventListener("mousemove", onMouseMove);
                                window.removeEventListener("mouseup", onMouseUp);
                              };

                              window.addEventListener("mousemove", onMouseMove);
                              window.addEventListener("mouseup", onMouseUp);
                            }}
                          >
                            {/* Time ticks — every 10s plus video end */}
                            {(() => {
                              const ticks = [];
                              for (let t = 0; t <= videoDuration; t += 10) ticks.push(t);
                              const end = Math.floor(videoDuration);
                              if (end % 10 !== 0) ticks.push(end);
                              return ticks;
                            })().map((t) => {
                              const pct = (t / videoDuration) * 100;
                              const isEnd = pct > 90;
                              return (
                                <Box key={t} sx={{
                                  position: "absolute",
                                  left: `${pct}%`,
                                  top: 0, bottom: 0,
                                  borderLeft: "1px solid rgba(255,255,255,0.15)",
                                }}>
                                  <Typography variant="caption" sx={{
                                    position: "absolute", top: 1, fontSize: "0.6rem", opacity: 0.5,
                                    ...(isEnd ? { right: 2 } : { left: 2 }),
                                  }}>
                                    {formatTime(t)}
                                  </Typography>
                                </Box>
                              );
                            })}

                            {/* Playback cursor — shows current video position */}
                            <Box sx={{
                              position: "absolute",
                              left: `${(videoCurrentTime / videoDuration) * 100}%`,
                              top: 0,
                              bottom: 0,
                              width: 2,
                              bgcolor: "#fff",
                              zIndex: 2,
                              pointerEvents: "none",
                              opacity: 0.9,
                            }} />

                            {/* Music clip block */}
                            <Box sx={{
                              position: "absolute",
                              top: 4,
                              bottom: 4,
                              left: `${(musicVideoOffset / videoDuration) * 100}%`,
                              width: `${((musicEndTime - musicStartTime) / videoDuration) * 100}%`,
                              minWidth: 20,
                              bgcolor: "rgba(255, 238, 32, 0.3)",
                              border: "1px solid rgba(255, 238, 32, 0.6)",
                              borderRadius: "4px",
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "&:active": { cursor: "grabbing" },
                            }}>
                              {/* Left resize handle */}
                              <Box sx={{
                                position: "absolute", left: 0, top: 0, bottom: 0, width: 10,
                                cursor: "ew-resize",
                                bgcolor: "rgba(255, 238, 32, 0.6)",
                                borderRadius: "4px 0 0 4px",
                              }} />
                              {/* Label — show video times since this is the video timeline */}
                              <Typography variant="caption" sx={{ color: "#ffee20", fontSize: "0.6rem", fontWeight: 600, pointerEvents: "none" }}>
                                {formatTime(musicVideoOffset)} → {formatTime(musicVideoOffset + (musicEndTime - musicStartTime))}
                              </Typography>
                              {/* Right resize handle */}
                              <Box sx={{
                                position: "absolute", right: 0, top: 0, bottom: 0, width: 10,
                                cursor: "ew-resize",
                                bgcolor: "rgba(255, 238, 32, 0.6)",
                                borderRadius: "0 4px 4px 0",
                              }} />
                            </Box>
                          </Box>

                          {/* Timeline info */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="caption" sx={{ opacity: 0.5 }}>
                              Song clip {formatTime(musicStartTime)}–{formatTime(musicEndTime)} plays at {formatTime(musicVideoOffset)} in video
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.5 }}>
                              Video: {formatTime(videoDuration)}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Mute original video audio toggle */}
                      {videoDuration > 0 && waveformReady && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5, px: 0.5, width: videoRenderedWidth > 0 ? videoRenderedWidth : "100%" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {muteVideoAudio ? (
                              <VolumeOffIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                            ) : (
                              <VolumeUpIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                            )}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Mute original video audio
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.5 }}>
                                {muteVideoAudio ? "Only the selected song will play" : "Original audio + song will be mixed together"}
                              </Typography>
                            </Box>
                          </Box>
                          <Switch
                            checked={muteVideoAudio}
                            onChange={(e) => setMuteVideoAudio(e.target.checked)}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#ffee20" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#ffee20" } }}
                          />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <>
                      <OutlinedInput
                        placeholder="Search for a song..."
                        value={musicQuery}
                        onChange={(e) => handleMusicSearch(e.target.value)}
                        fullWidth
                        startAdornment={<SearchIcon sx={{ mr: 1, opacity: 0.5 }} />}
                        endAdornment={musicSearching ? <CircularProgress size={20} /> : null}
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: 2,
                        }}
                      />
                      {musicResults.length > 0 && (
                        <List sx={{
                          maxHeight: 200,
                          overflow: "auto",
                          bgcolor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: 1,
                          mt: 1,
                        }}>
                          {musicResults.map((music) => (
                            <ListItem
                              button
                              key={music._id}
                              onClick={() => {
                                setSelectedMusic(music);
                                setMusicResults([]);
                                setMusicQuery("");
                              }}
                              sx={{
                                "&:hover": { bgcolor: "rgba(255, 238, 32, 0.1)" },
                                borderRadius: 1,
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  src={music.thumbnailImg?.filePath}
                                  variant="rounded"
                                  sx={{ width: 40, height: 40 }}
                                >
                                  <MusicNoteIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={music.title}
                                secondary={music.uploader?.displayArtistName || `${music.uploader?.firstName || ""} ${music.uploader?.lastName || ""}`.trim() || "Unknown"}
                                primaryTypographyProps={{ noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true, sx: { opacity: 0.7 } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !values.videoFile || !values.title || !values.studioId}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    backgroundColor: "#ffee20",
                    color: "#000",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    "&:hover": {
                      backgroundColor: "#e6d61c",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(255, 238, 32, 0.3)",
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  }}
                >
                  {loading ? "Uploading..." : "Upload Video"}
                </Button>
              </Stack>
            </form>
          )}
        </Formik>
      </Box>

      {loading && <Loader message="Uploading your video..." />}
      
      <SuccessPopup
        open={success}
        handleClose={handleClose}
        message={t("Video uploaded successfully!")}
      />
    </Box>
  );
}